<template>
  <div class="betrieb-view">
    <div v-if="locationReady && locationID !== 'NB'" class="card">
      <div class="card-body">
        <el-alert
          title="Diese Ansicht ist vorerst nur für den Standort Neroberg verfügbar."
          type="warning"
          :closable="false"
          show-icon
        ></el-alert>
      </div>
    </div>
    <template v-else-if="locationReady">
      <!-- Header -->
      <div class="card header-card">
        <div class="card-body d-flex flex-column flex-md-row justify-content-between align-items-md-center">
          <div>
            <h4 class="card-title mb-1">
              <i class="bi bi-sunrise"></i> Betriebsbeginn
            </h4>
            <p class="text-muted mb-0">{{ todayLabel }}</p>
          </div>
          <div class="save-indicator mt-2 mt-md-0">
            <span v-if="saving" class="text-warning">
              <i class="el-icon-loading"></i> Speichert&hellip;
            </span>
            <span v-else-if="lastSavedAt" class="text-success">
              <i class="bi bi-check-circle-fill"></i> Zwischengespeichert {{ lastSavedLabel }}
            </span>
            <span v-else class="text-muted">
              <i class="bi bi-cloud"></i> Bereit
            </span>
          </div>
        </div>
      </div>

      <!-- Öffnungszeit -->
      <div class="card section-card" v-loading="loading">
        <div class="card-header">
          <h5 class="card-title mb-0"><i class="bi bi-clock"></i> Öffnungszeit</h5>
        </div>
        <div class="card-body">
          <el-time-select
            v-model="open"
            value-format="HH:mm"
            :picker-options="{ start: '08:00', step: '00:15', end: '14:00' }"
            placeholder="Öffnungszeit wählen"
            class="time-input"
          ></el-time-select>
        </div>
      </div>

      <!-- Zustand der Parcours -->
      <div class="card section-card" v-loading="loading">
        <div class="card-header">
          <h5 class="card-title mb-0"><i class="bi bi-map"></i> Zustand der Parcours</h5>
        </div>
        <div class="card-body">
          <p v-if="!parcours.length && !loading" class="text-muted mb-0">
            Keine Parcours gefunden.
          </p>
          <div
            v-for="(p, idx) in parcours"
            :key="p.id"
            class="parcours-row"
          >
            <div class="parcours-row__title">
              <span class="badge badge-primary mr-2">{{ idx + 1 }}</span>
              <strong>{{ p.Name }}</strong>
            </div>
            <div class="row no-gutters parcours-row__fields">
              <div class="col-12 col-md-4 pr-md-2 pb-2 pb-md-0">
                <label class="small text-muted mb-1">Prüfer</label>
                <el-select
                  v-model="p.Inspector"
                  placeholder="Prüfer wählen"
                  filterable
                  size="small"
                  class="w-100"
                  @change="onInspectorChange(idx, $event)"
                >
                  <el-option
                    v-for="t in trainer"
                    :key="t.trainerName"
                    :value="t.trainerName"
                    :label="t.trainerName"
                  ></el-option>
                </el-select>
              </div>
              <div class="col-12 col-md-4 pr-md-2 pb-2 pb-md-0">
                <label class="small text-muted mb-1">Zustand</label>
                <el-select
                  v-model="p.Condition"
                  placeholder="Zustand wählen"
                  size="small"
                  class="w-100"
                >
                  <el-option value="1" label="Gut"></el-option>
                  <el-option value="2" label="Muss beobachtet werden"></el-option>
                  <el-option value="3" label="Mängel"></el-option>
                  <el-option value="4" label="Gesperrt wegen erheblicher Mängel"></el-option>
                </el-select>
              </div>
              <div class="col-12 col-md-4">
                <label class="small text-muted mb-1">Anmerkung</label>
                <el-input
                  v-model="p.Note"
                  type="textarea"
                  autosize
                  size="small"
                  placeholder="Anmerkung (falls nicht 'Gut')"
                  :disabled="p.Condition === '1'"
                ></el-input>
              </div>
            </div>
          </div>
        </div>
      </div>

      <!-- HT-Checkliste -->
      <div class="card section-card" v-loading="loading">
        <div class="card-header">
          <h5 class="card-title mb-0">
            <i class="bi bi-list-check"></i> Checkliste HT Betriebsbeginn
          </h5>
        </div>
        <div class="card-body">
          <p v-if="!checklistItems.length && !loading" class="text-muted mb-0">
            Keine Checklistenpunkte gefunden.
          </p>
          <div
            v-for="item in checklistItems"
            :key="item.key"
            class="checklist-row"
            :class="{ 'checklist-row--prev-missed': item.prevUnchecked }"
          >
            <p-checkbox
              v-model="item.checked"
              class="checklist-row__check"
            >{{ item.checkItem }}</p-checkbox>
            <button
              v-if="!item.noteOpen && !item.note"
              type="button"
              class="checklist-row__note-toggle"
              title="Anmerkung hinzufügen"
              aria-label="Anmerkung hinzufügen"
              @click="toggleNote(item)"
            >
              <i class="bi bi-chat-left-text"></i>
            </button>
            <div
              class="checklist-row__input"
              v-if="item.fieldType >= 2"
            >
              <el-input
                v-if="item.fieldType === 2"
                type="number"
                min="0"
                size="small"
                v-model="item.inputValue"
                placeholder="0"
                :disabled="!item.checked"
              ></el-input>
              <el-input
                v-else-if="item.fieldType === 4"
                type="text"
                size="small"
                v-model="item.inputValue"
                placeholder="Bitte eingeben"
                :disabled="!item.checked"
              ></el-input>
              <el-select
                v-else-if="item.fieldType === 3"
                v-model="item.inputValue"
                filterable
                size="small"
                placeholder="Mitarbeiter wählen"
                class="w-100"
                :disabled="!item.checked"
              >
                <el-option
                  v-for="t in trainer"
                  :key="t.trainerName"
                  :value="t.trainerName"
                  :label="t.trainerName"
                ></el-option>
              </el-select>
            </div>
            <div v-if="item.prevUnchecked" class="checklist-row__alert">
              <i class="bi bi-exclamation-triangle-fill"></i>
              Am {{ item.prevDate }} nicht erledigt
            </div>
            <div v-if="item.noteOpen || item.note" class="checklist-row__note-editor">
              <div class="checklist-row__note-header">
                <span class="small text-muted">
                  <i class="bi bi-chat-left-text"></i> Anmerkung
                </span>
                <span
                  v-if="item.prevNoteDate && item.note && item.note === item.prevNoteText"
                  class="small text-warning ml-2"
                >
                  (übernommen vom {{ item.prevNoteDate }})
                </span>
              </div>
              <el-input
                type="textarea"
                :autosize="{ minRows: 2, maxRows: 6 }"
                size="small"
                v-model="item.note"
                placeholder="Anmerkung eingeben..."
                @input="onNoteInput(item)"
              ></el-input>
              <button
                v-if="item.note"
                type="button"
                class="btn btn-link btn-sm text-danger checklist-row__note-clear"
                @click="clearNote(item)"
              >
                <i class="bi bi-x-circle"></i> Anmerkung entfernen
              </button>
            </div>
          </div>
        </div>
      </div>

      <!-- Ausgefüllt von -->
      <div class="card section-card">
        <div class="card-body">
          <div class="row align-items-center">
            <label class="col-sm-4 col-form-label">Ausgefüllt von</label>
            <div class="col-sm-8">
              <el-select
                v-model="processedBy"
                placeholder="Trainer wählen"
                filterable
                class="w-100"
              >
                <el-option
                  v-for="t in trainer"
                  :key="t.trainerName"
                  :value="t.trainerName"
                  :label="t.trainerName"
                ></el-option>
              </el-select>
            </div>
          </div>
          <p class="small text-muted mt-3 mb-0">
            <i class="bi bi-info-circle"></i>
            Eingaben werden automatisch zwischengespeichert. Die Ansicht kann während des aktuellen
            Tages jederzeit erneut geöffnet und angepasst werden.
          </p>
        </div>
      </div>
    </template>
  </div>
</template>

<script>
import Vue from 'vue'
import { db, Timestamp } from '../../../../firebaseDb'
import { Input, Select, Option, TimeSelect, Alert, Loading } from 'element-ui'
import moment from 'moment-timezone'
import store from '../../../../store'

Vue.use(TimeSelect)
Vue.use(Loading.directive)

export default {
  components: {
    [Input.name]: Input,
    [Select.name]: Select,
    [Option.name]: Option,
    [TimeSelect.name]: TimeSelect,
    [Alert.name]: Alert
  },
  data() {
    return {
      loading: true,
      dataLoaded: false,
      saving: false,
      lastSavedAt: null,
      nowTick: 0,
      nowTimer: null,
      saveTimer: null,
      reportName: '',
      todayLabel: '',
      open: '',
      processedBy: '',
      parcours: [],
      checklistItems: [],
      trainer: [],
      prevFirstInspector: '',
      unwatchLocation: null
    }
  },
  computed: {
    locationID() {
      return store.getters.userlocation
    },
    locationReady() {
      // userlocation defaults to 0 until set by auth callback
      const l = this.locationID
      return l !== 0 && l !== null && l !== undefined
    },
    lastSavedLabel() {
      // re-evaluate when nowTick changes
      // eslint-disable-next-line no-unused-vars
      const _ = this.nowTick
      if (!this.lastSavedAt) return ''
      const secs = Math.max(0, Math.round((Date.now() - this.lastSavedAt.getTime()) / 1000))
      if (secs < 5) return 'gerade eben'
      if (secs < 60) return 'vor ' + secs + ' Sekunden'
      const mins = Math.floor(secs / 60)
      if (mins === 1) return 'vor 1 Minute'
      if (mins < 60) return 'vor ' + mins + ' Minuten'
      const hours = Math.floor(mins / 60)
      if (hours === 1) return 'vor 1 Stunde'
      return 'vor ' + hours + ' Stunden'
    }
  },
  created() {
    moment.locale('de')
    this.todayLabel = moment().format('dddd, DD. MMMM YYYY')
    this.nowTimer = setInterval(() => {
      this.nowTick++
    }, 15000)
    if (this.locationReady) {
      this.initForLocation()
    } else {
      this.unwatchLocation = store.watch(
        (state) => state.userlocation,
        () => {
          if (this.locationReady) {
            if (this.unwatchLocation) {
              this.unwatchLocation()
              this.unwatchLocation = null
            }
            this.initForLocation()
          }
        }
      )
    }
  },
  beforeDestroy() {
    if (this.nowTimer) {
      clearInterval(this.nowTimer)
      this.nowTimer = null
    }
    if (this.unwatchLocation) {
      this.unwatchLocation()
      this.unwatchLocation = null
    }
    // flush pending save
    if (this.saveTimer) {
      clearTimeout(this.saveTimer)
      this.saveTimer = null
      if (this.dataLoaded) {
        this.persist()
      }
    }
  },
  methods: {
    initForLocation() {
      if (this.locationID !== 'NB') {
        this.loading = false
        return
      }
      this.reportName = moment().format('YYYYMMDD') + '-NB'
      this.loadAll()
    },
    async loadTrainer() {
      const snap = await db
        .collection('trainer')
        .where('locationID', 'array-contains', 'NB')
        .where('isToday', '==', true)
        .where('isTodayLocation', '==', 'NB')
        .orderBy('Name')
        .get()
      this.trainer = []
      snap.forEach((doc) => {
        this.trainer.push({ trainerName: doc.data().Name })
      })
    },
    async loadAll() {
      this.loading = true
      try {
        await this.loadTrainer()
        // 1) parcours definitions
        const parcSnap = await db
          .collection('parcours')
          .where('locationID', '==', 'NB')
          .orderBy('position')
          .get()
        const parcoursList = []
        parcSnap.forEach((doc) => {
          parcoursList.push({
            id: doc.id,
            Name: doc.data().Name,
            Position: doc.data().position,
            Inspector: '',
            Condition: '1',
            Note: ''
          })
        })

        // 2) checklist items for HTStart
        const itemsSnap = await db
          .collection('checklists/HTStart/Items')
          .where('locationID', '==', 'NB')
          .orderBy('position')
          .get()
        const items = []
        const today = moment().isoWeekday()
        itemsSnap.forEach((doc) => {
          const storedWeekdays = doc.data().weekdays
          const weekdays = Array.isArray(storedWeekdays) && storedWeekdays.length > 0
            ? storedWeekdays.map((d) => Number(d))
            : [1, 2, 3, 4, 5, 6, 7]
          if (!weekdays.includes(today)) {
            return
          }
          items.push({
            key: doc.id,
            checkItem: doc.data().Item,
            fieldType: Number(doc.data().fieldType) || 1,
            position: doc.data().position,
            checked: false,
            inputValue: '',
            note: '',
            noteCleared: false,
            noteOpen: false,
            prevUnchecked: false,
            prevDate: '',
            prevNoteText: '',
            prevNoteDate: ''
          })
        })

        // 3) existing tagesreport doc (open time)
        const reportDoc = await db.collection('tagesreports').doc(this.reportName).get()
        if (reportDoc.exists) {
          const d = reportDoc.data()
          if (d.Open) this.open = d.Open
        }

        // 4) existing Parcours subcollection
        const parcSubSnap = await db
          .collection('tagesreports/' + this.reportName + '/Parcours')
          .get()
        const parcByName = {}
        parcSubSnap.forEach((doc) => {
          const d = doc.data()
          parcByName[d.Name] = d
        })
        parcoursList.forEach((p) => {
          const existing = parcByName[p.Name]
          if (existing) {
            p.Inspector = existing.Inspector || ''
            p.Condition = String(existing.Condition || '1')
            p.Note = existing.Note || ''
          }
        })

        // 5) existing HTStart subcollection
        const htSnap = await db
          .collection('tagesreports/' + this.reportName + '/HTStart')
          .get()
        const htByPos = {}
        let storedProcessedBy = ''
        htSnap.forEach((doc) => {
          if (doc.id === 'trainer') {
            storedProcessedBy = doc.data().processedBy || ''
          } else {
            const d = doc.data()
            htByPos[d.position] = d
          }
        })
        items.forEach((it) => {
          const existing = htByPos[it.position]
          if (existing) {
            it.checked = !!existing.checked
            it.inputValue = existing.value != null ? existing.value : ''
            it.note = existing.note != null ? existing.note : ''
            it.noteCleared = !!existing.noteCleared
          }
        })

        // 6) carry-over: previous days' state and notes for highlight + carry-over
        const prior = await this.loadPriorChecklistState('HTStart', 14)
        items.forEach((it) => {
          const existing = htByPos[it.position]
          const pp = prior[Number(it.position)]
          if (!pp) return
          if (pp.firstFound && !pp.firstChecked) {
            it.prevUnchecked = true
            it.prevDate = pp.firstDate
          }
          if (pp.latestNote) {
            it.prevNoteText = pp.latestNote
            it.prevNoteDate = pp.latestNoteDate
            // Only carry over note if there's no existing note document for today
            if (!existing && !it.note) {
              it.note = pp.latestNote
            }
          }
          if (it.note) {
            it.noteOpen = true
          }
        })

        this.parcours = parcoursList
        this.checklistItems = items
        this.processedBy = storedProcessedBy
        this.prevFirstInspector = parcoursList.length ? parcoursList[0].Inspector : ''
      } catch (err) {
        console.error('Betriebsbeginn loadAll error', err)
        this.$notify({
          message: 'Daten konnten nicht geladen werden: ' + err.message,
          type: 'danger',
          horizontalAlign: 'right',
          verticalAlign: 'bottom'
        })
      } finally {
        this.loading = false
        this.$nextTick(() => {
          this.dataLoaded = true
        })
      }
    },
    async loadPriorChecklistState(subcollection, maxDays) {
      // Returns { [position]: { firstFound, firstChecked, firstDate, latestNote, latestNoteDate } }
      // Uses asc order to match the existing (locationID asc, Date asc) composite index;
      // we sort to most-recent-first afterwards.
      try {
        const startOfToday = moment().startOf('day').toDate()
        const cutoff = moment().subtract(maxDays, 'days').startOf('day').toDate()
        const reportSnap = await db
          .collection('tagesreports')
          .where('locationID', '==', 'NB')
          .where('Date', '>=', Timestamp.fromDate(cutoff))
          .where('Date', '<', Timestamp.fromDate(startOfToday))
          .orderBy('Date', 'asc')
          .get()
        const reports = []
        reportSnap.forEach((d) => {
          const data = d.data()
          if (data && data.Date && typeof data.Date.toDate === 'function') {
            reports.push({ id: d.id, date: data.Date.toDate() })
          }
        })
        if (!reports.length) return {}
        // Most recent first
        reports.sort((a, b) => b.date.getTime() - a.date.getTime())
        const subs = await Promise.all(
          reports.map((r) =>
            db.collection('tagesreports/' + r.id + '/' + subcollection).get()
          )
        )
        const byPosition = {}
        reports.forEach((rep, idx) => {
          const dateStr = moment(rep.date).format('DD.MM.')
          subs[idx].forEach((doc) => {
            if (doc.id === 'trainer') return
            const data = doc.data() || {}
            if (data.position === undefined || data.position === null) return
            const pos = Number(data.position)
            if (Number.isNaN(pos)) return
            if (!byPosition[pos]) {
              byPosition[pos] = {
                firstFound: true,
                firstChecked: !!data.checked,
                firstDate: dateStr,
                latestNote: data.note ? String(data.note) : '',
                latestNoteDate: data.note ? dateStr : '',
                noteStateFound: !!data.note || !!data.noteCleared
              }
            } else if (!byPosition[pos].noteStateFound && (data.note || data.noteCleared)) {
              byPosition[pos].latestNote = String(data.note)
              byPosition[pos].latestNoteDate = data.note ? dateStr : ''
              byPosition[pos].noteStateFound = true
            }
          })
        })
        return byPosition
      } catch (err) {
        console.error('loadPriorChecklistState failed', err)
        return {}
      }
    },
    toggleNote(item) {
      item.noteOpen = !item.noteOpen
    },
    clearNote(item) {
      item.note = ''
      item.noteCleared = true
      item.noteOpen = false
    },
    onNoteInput(item) {
      item.noteCleared = !item.note
    },
    onInspectorChange(idx, newVal) {
      if (idx !== 0) return
      // propagate to other rows that are still empty
      for (let i = 1; i < this.parcours.length; i++) {
        const cur = this.parcours[i].Inspector
        if (!cur) {
          this.parcours[i].Inspector = newVal
        }
      }
      this.prevFirstInspector = newVal
    },
    scheduleSave() {
      if (!this.dataLoaded) return
      if (this.saveTimer) clearTimeout(this.saveTimer)
      this.saving = true
      this.saveTimer = setTimeout(() => {
        this.saveTimer = null
        this.persist()
      }, 800)
    },
    async persist() {
      if (!this.dataLoaded) {
        this.saving = false
        return
      }
      try {
        const batch = db.batch()
        const reportRef = db.collection('tagesreports').doc(this.reportName)
        batch.set(
          reportRef,
          {
            Date: Timestamp.fromDate(new Date()),
            locationID: 'NB',
            Open: this.open || ''
          },
          { merge: true }
        )

        const parcCol = reportRef.collection('Parcours')
        this.parcours.forEach((p) => {
          batch.set(parcCol.doc(p.id), {
            Name: p.Name,
            Inspector: p.Inspector || '',
            Condition: p.Condition || '1',
            Position: p.Position,
            Note: p.Note || ''
          })
        })

        const htCol = reportRef.collection('HTStart')
        this.checklistItems.forEach((it) => {
          batch.set(htCol.doc('item-' + it.position), {
            Task: it.checkItem,
            checked: !!it.checked,
            value: it.inputValue || '',
            note: it.note || '',
            noteCleared: !it.note && !!it.noteCleared,
            position: it.position
          })
        })
        batch.set(htCol.doc('trainer'), {
          processedBy: this.processedBy || '',
          position: 9999
        })

        await batch.commit()
        this.lastSavedAt = new Date()
        this.nowTick++
      } catch (err) {
        console.error('Betriebsbeginn persist error', err)
        this.$notify({
          message: 'Zwischenspeichern fehlgeschlagen: ' + err.message,
          type: 'danger',
          horizontalAlign: 'right',
          verticalAlign: 'bottom'
        })
      } finally {
        this.saving = false
      }
    }
  },
  watch: {
    open() {
      this.scheduleSave()
    },
    processedBy() {
      this.scheduleSave()
    },
    parcours: {
      deep: true,
      handler() {
        this.scheduleSave()
      }
    },
    checklistItems: {
      deep: true,
      handler() {
        this.scheduleSave()
      }
    }
  }
}
</script>

<style lang="scss" scoped>
.betrieb-view {
  max-width: 960px;
  margin: 0 auto;
  padding: 0 4px;
}
.betrieb-view .card {
  margin-bottom: 1.75rem;
}
.header-card .card-title {
  color: #2c3e50;
}
.header-card .card-body {
  padding-top: 0rem;
  padding-bottom: 1rem;
}
.section-card {
  margin-bottom: 1.75rem;
}
.section-card .card-header {
  background: transparent;
  border-bottom: 1px solid #eee;
  padding: 0.85rem 1.25rem;
}
.save-indicator {
  font-size: 0.9rem;
  font-weight: 500;
  i {
    margin-right: 4px;
  }
}
.time-input {
  width: 100%;
  max-width: 220px;
}
.parcours-row {
  padding: 0.85rem 0;
  border-bottom: 1px dashed #eee;
}
.parcours-row:last-child {
  border-bottom: none;
}
.parcours-row__title {
  margin-bottom: 0.55rem;
  font-size: 1rem;
}
.checklist-row {
  display: flex;
  align-items: center;
  column-gap: 16px;
  row-gap: 10px;
  padding: 0.75rem 12px;
  border-bottom: 1px dashed #f0f0f0;
  border-radius: 6px;
  flex-wrap: wrap;
  transition: background-color 0.2s ease;
}
.checklist-row:last-child {
  border-bottom: none;
}
.checklist-row--prev-missed {
  background-color: rgba(255, 193, 7, 0.10);
  box-shadow: inset 3px 0 0 #f0a300;
}
.checklist-row__check {
  flex: 1 1 260px;
  min-width: 0;
  margin: 0 !important;
}
.checklist-row__input {
  flex: 0 0 240px;
  min-width: 200px;
}
.checklist-row__alert {
  flex: 1 1 100%;
  font-size: 0.85rem;
  color: #b76e00;
  display: flex;
  align-items: center;
  gap: 6px;
}
.checklist-row__note-toggle {
  flex: 0 0 auto;
  display: inline-flex;
  align-items: center;
  justify-content: center;
  width: 30px;
  height: 30px;
  padding: 0;
  border: 1px solid transparent;
  border-radius: 50%;
  background: transparent;
  color: #9aa0a6;
  font-size: 1rem;
  cursor: pointer;
  transition: color 0.15s ease, background-color 0.15s ease, border-color 0.15s ease;
}
.checklist-row__note-toggle:hover,
.checklist-row__note-toggle:focus {
  color: #15A34A;
  background-color: rgba(21, 163, 74, 0.08);
  border-color: rgba(21, 163, 74, 0.25);
  outline: none;
}
.checklist-row__note-editor {
  flex: 1 1 100%;
  background: #fafafa;
  border: 1px solid #ececec;
  border-radius: 6px;
  padding: 10px 12px;
  margin-top: 4px;
}
.checklist-row__note-header {
  margin-bottom: 4px;
  display: flex;
  align-items: center;
  flex-wrap: wrap;
  i {
    margin-right: 4px;
  }
}
.checklist-row__note-clear {
  padding-left: 0;
  padding-right: 0;
  font-size: 0.8rem;
  margin-top: 4px;
  text-decoration: none;
  i {
    margin-right: 4px;
  }
}
@media (max-width: 575.98px) {
  .checklist-row__input {
    flex: 1 1 100%;
  }
}
.betrieb-view ::v-deep .el-select .el-select__tags {
  padding-left: 15px;
}
.betrieb-view ::v-deep .el-textarea__inner {
  padding-left: 15px;
}
</style>
